import {type ComponentType, type ReactNode, useState} from 'react';
import {type LucideIcon} from 'lucide-react';
import {DropdownMenu, DropdownMenuContent, DropdownMenuTrigger} from '../dropdown-menu';
import {cn} from '../lib/cn';

export type LucideIconEntry = {
  type: 'lucide';
  name: string;
  icon: LucideIcon;
};

export type CustomIconEntry = {
  type: 'custom';
  name: string;
  icon: ComponentType<{size?: number; color?: string}>;
};

export type IconEntry = LucideIconEntry | CustomIconEntry;

export type IconsDropdownProps = {
  icons: IconEntry[];
  onChange: (iconName: string) => void;
  columns?: number;
  selectedIcon?: string;
  children: ReactNode;
  position?: 'left' | 'right';
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function IconsDropdown({
  icons,
  onChange,
  columns = 4,
  selectedIcon,
  children,
  position = 'left',
  defaultOpen,
  onOpenChange: onOpenChangeProp,
}: IconsDropdownProps) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  function handleOpenChange(value: boolean) {
    setOpen(value);
    onOpenChangeProp?.(value);
  }

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={position === 'right' ? 'end' : 'start'}
        className="max-h-48 w-auto overflow-y-auto p-6 shadow-[0px_5px_15px_rgba(26,148,255,0.16)]"
      >
        <div
          className="grid gap-1"
          style={{gridTemplateColumns: `repeat(${columns}, 1fr)`}}
        >
          {icons.map(entry => {
            const isSelected = entry.name === selectedIcon;
            const iconColor = isSelected
              ? 'var(--chekin-color-brand-blue)'
              : 'var(--chekin-color-gray-1)';
            const IconComponent = entry.icon;

            return (
              <button
                key={entry.name}
                type="button"
                onClick={() => {
                  onChange(entry.name);
                  handleOpenChange(false);
                }}
                className={cn(
                  'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors',
                  'hover:bg-[var(--chekin-color-surface-input-empty)]',
                  isSelected &&
                    'bg-[color-mix(in_srgb,var(--chekin-color-brand-blue)_10%,transparent)]',
                )}
              >
                <IconComponent size={24} color={iconColor} />
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
